import FroalaEditor from 'froala-editor';
import { Maybe, fromNullable, just, nothing } from 'maybeasy';
import { useEffect, useState } from 'react';
import ReactFroalaEditor from 'react-froala-wysiwyg';
import './Configure';
import { setReadonlyState, toolbarButtons } from './Types';

interface Props {
  value: string | null | undefined;
  onChange: (content: string) => void;
  placeholder?: string;
  readonly?: boolean;
  backgroundColor?: string;
}

export function RichTextEditor(props: Props) {
  const [editorInstance, setEditorInstance] = useState<Maybe<FroalaEditor>>(nothing);

  useEffect(() => {
    editorInstance
      .andThen(({ edit }) => fromNullable(edit))
      .do((edit) => setReadonlyState(edit, props.readonly));
  }, [props.readonly, editorInstance]);

  return (
    <ReactFroalaEditor
      model={props.value}
      onModelChange={props.onChange}
      config={{
        toolbarButtons,
        heightMax: 384, // pixel conversion for class `max-h-96`
        heightMin: 192,
        placeholderText: props.placeholder,
        linkEditButtons: ['linkOpen', 'linkEdit', 'linkRemove'],
        linkInsertButtons: ['linkBack'],
        linkAutoPrefix: 'https://',
        listAdvancedTypes: false,
        // Allow semantic HTML tags for proper paragraph formatting
        htmlAllowedTags: [
          'p',
          'br',
          'strong',
          'em',
          'u',
          's',
          'a',
          'img',
          'ul',
          'ol',
          'li',
          'h1',
          'h2',
          'h3',
          'h4',
          'h5',
          'h6',
          'hr',
          'span',
          'div',
          'table',
          'thead',
          'tbody',
          'tr',
          'th',
          'td',
        ],
        // Allow attributes on HTML tags to preserve list formatting and other styling
        htmlAllowedAttrs: [
          'href',
          'title',
          'target',
          'rel',
          'src',
          'alt',
          'width',
          'height',
          'start',
          'type',
          'value',
          'border',
          'colspan',
          'rowspan',
          'class',
          'id',
          'style',
        ],
        key: import.meta.env['VITE_FROALA_LICENSE_KEY'],
        attribution: false,
        events: {
          initialized: function () {
            /**
             * Froala has no config types, so we have no choice but to type assert.
             */
            const editor = this as unknown as FroalaEditor;
            editor.$el[0].style.backgroundColor = props.backgroundColor || '#E4E4E7';

            setEditorInstance(just(editor));
          },
        },
      }}
    />
  );
}
